import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, CheckCircle2, Clock, Gift, Crown,
  AlertCircle, Shield, Loader2, XCircle, RotateCcw, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Subscription = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [polling, setPolling] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/subscription-status`);
      setSubStatus(res.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) {
      setPolling(false);
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/payments/checkout-status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPolling(false);
        toast.success('Payment successful! Your subscription is now active.');
        fetchData();
        window.history.replaceState({}, '', '/subscription');
        return;
      } else if (res.data.status === 'expired') {
        setPolling(false);
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/subscription');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      // silent
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setPolling(true);
      pollPaymentStatus(sessionId);
    }
  }, [fetchData, searchParams, pollPaymentStatus]);

  const handleSubscribe = async (plan) => {
    setProcessing(plan);
    try {
      const res = await axios.post(`${API_URL}/api/payments/create-checkout`, {
        origin_url: window.location.origin,
        plan
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/cancel-subscription`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/reactivate-subscription`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Subscription</h1>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      </div>
    );
  }

  const isActive = subStatus?.active;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const cancelPending = subStatus?.cancel_pending;
  const rewards = subStatus?.referral_rewards;
  const isPremium = subStatus?.type === 'premium';
  const isTrial = subStatus?.type === 'trial' || subStatus?.type === 'beta_trial';
  const paidPlanLabel = subStatus?.plan === 'yearly' ? 'Yearly (£45/yr)' : 'Monthly (£3.99/mo)';
  const planLabel = isPremium
    ? paidPlanLabel
    : isTrial
      ? `Free trial — ${subStatus?.days_remaining ?? 0} day${subStatus?.days_remaining === 1 ? '' : 's'} remaining`
      : null;

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <CreditCard className="h-7 w-7 text-amber-400" />
          Subscription
        </h1>
        <p className="text-slate-400 mt-1 text-sm">Your plan and billing details</p>
      </div>

      {/* Polling indicator */}
      {polling && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-amber-400 animate-spin" />
            <p className="text-amber-400 text-sm font-medium">Verifying your payment...</p>
          </CardContent>
        </Card>
      )}

      {/* Single Subscription Card */}
      <Card className={`bg-slate-800 border-slate-700 ${isActive ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`} data-testid="subscription-card">
        <CardContent className="p-5 sm:p-6 space-y-5">
          {/* Status Badge Row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {isAdmin ? (
                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                  <Shield className="h-3 w-3 mr-1" />Admin Access
                </Badge>
              ) : isActive && isTrial ? (
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30" data-testid="trial-badge">
                  <Clock className="h-3 w-3 mr-1" />Free Trial
                </Badge>
              ) : isActive ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Active
                </Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  <AlertCircle className="h-3 w-3 mr-1" />Expired
                </Badge>
              )}
              {cancelPending && (
                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                  Cancels at period end
                </Badge>
              )}
            </div>
            {/* Actions */}
            <div className="flex gap-2">
              {!isAdmin && isActive && !cancelPending && subStatus?.type === 'premium' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="border-slate-600 text-slate-400 hover:text-red-400 hover:border-red-500/50 h-8 text-xs"
                  data-testid="cancel-subscription-btn"
                >
                  {cancelling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                  Cancel Renewal
                </Button>
              )}
              {!isAdmin && cancelPending && (
                <Button
                  size="sm"
                  onClick={handleReactivate}
                  disabled={cancelling}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs"
                  data-testid="reactivate-subscription-btn"
                >
                  {cancelling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                  Resume Auto-Renewal
                </Button>
              )}
            </div>
          </div>

          {/* Detail Rows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex justify-between sm:flex-col sm:gap-0.5">
              <span className="text-slate-500 text-xs">Plan</span>
              <span className="text-white font-medium" data-testid="sub-plan">
                {isAdmin ? 'Admin (Full Access)' : planLabel || 'No active plan'}
              </span>
            </div>
            <div className="flex justify-between sm:flex-col sm:gap-0.5">
              <span className="text-slate-500 text-xs">Signed Up</span>
              <span className="text-white font-medium flex items-center gap-1.5" data-testid="sub-signup-date">
                <Calendar className="h-3.5 w-3.5 text-slate-500" />
                {formatDate(subStatus?.signed_up)}
              </span>
            </div>
            <div className="flex justify-between sm:flex-col sm:gap-0.5">
              <span className="text-slate-500 text-xs">{isPremium ? 'Renewal Date' : isTrial ? 'Trial Ends' : 'Renewal Date'}</span>
              <span className="text-white font-medium flex items-center gap-1.5" data-testid="sub-renewal-date">
                <Clock className="h-3.5 w-3.5 text-slate-500" />
                {isAdmin ? 'N/A' : formatDate(subStatus?.renewal_date)}
              </span>
            </div>
            <div className="flex justify-between sm:flex-col sm:gap-0.5">
              <span className="text-slate-500 text-xs">Accrued Free Subscription</span>
              <span className="text-white font-medium flex items-center gap-1.5" data-testid="sub-accrued-free">
                <Gift className="h-3.5 w-3.5 text-cyan-400" />
                {rewards && rewards.total_reward_days > 0
                  ? <span className="text-cyan-400">{rewards.reward_weeks} week{rewards.reward_weeks !== 1 ? 's' : ''} ({rewards.total_reward_days} days)</span>
                  : <span className="text-slate-500">None yet</span>
                }
              </span>
            </div>
          </div>

          {/* Referral note */}
          {rewards && rewards.successful_referrals > 0 && (
            <p className="text-xs text-slate-500 pt-1 border-t border-slate-700">
              Earned from {rewards.successful_referrals} successful referral{rewards.successful_referrals !== 1 ? 's' : ''}. Free weeks are applied at the end of your billing year.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Subscribe options — shown to anyone without an active paid subscription */}
      {!isAdmin && (!isPremium || cancelPending) && (
        <div className="space-y-3">
        {isTrial && !cancelPending && (
          <p className="text-slate-400 text-sm" data-testid="trial-subscribe-note">
            Subscribe now to keep uninterrupted access — any remaining trial days are added on top of your paid plan.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="plan-selection">
          {/* Monthly */}
          <Card className="bg-slate-800 border-slate-700 hover:border-amber-500/40 transition-colors">
            <CardContent className="p-5">
              <p className="text-slate-400 text-sm font-medium">Monthly</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-bold text-white">£3.99</span>
                <span className="text-slate-500 text-sm">/month</span>
              </div>
              <p className="text-slate-500 text-xs mt-1">Auto-renews monthly</p>
              <Button
                onClick={() => handleSubscribe('monthly')}
                disabled={processing !== null}
                className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="subscribe-monthly-btn"
              >
                {processing === 'monthly' ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <><Crown className="h-4 w-4 mr-2" />Subscribe Monthly</>
                )}
              </Button>
            </CardContent>
          </Card>
          {/* Yearly */}
          <Card className="bg-slate-800 border-amber-500/40 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-amber-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
              BEST VALUE
            </div>
            <CardContent className="p-5">
              <p className="text-slate-400 text-sm font-medium">Yearly</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-bold text-white">£45</span>
                <span className="text-slate-500 text-sm">/year</span>
              </div>
              <p className="text-amber-400 text-xs mt-1 font-medium">Save £2.88 vs monthly</p>
              <Button
                onClick={() => handleSubscribe('yearly')}
                disabled={processing !== null}
                className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="subscribe-yearly-btn"
              >
                {processing === 'yearly' ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <><Crown className="h-4 w-4 mr-2" />Subscribe Yearly</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
